import { createFeature, createReducer, on } from "@ngrx/store";
import { MipsPacket, MipsQuestionnaire } from "@models/mips-stepper.model";
import { MipsPacketActions } from "@store/actions/mips-packet/mips-packet.actions";
import { MipsQuestionnaireActions } from "@store/actions/mips-questionnaire/mips-questionnaire.actions";
import { parseAttestationBool } from "@services/mips-api.service";
import { DashboardProviderProd } from "@models/dashboard-providers.model";
import { OrganizationTin } from "@models/organizationTin.model";
import { DashboardMeasureDetailProd } from "@models/dashboard-measure-details.model";
import { ReweightingResult } from "@models/mips-reweighting.model";
import { enrichQualityData } from "../../../utils/quality-measure-enrichment.util";

export const mipsPacketFeatureKey = "mipsPacket";

export interface State {
  traditionalPacket: MipsPacket | null;
  mvpPacket: MipsPacket | null;
  providersForSelectedPractice: DashboardProviderProd[];
  loadingProviders: boolean;
  eligibilityCheckResult: {
    npi: string;
    organizationId: number;
    performanceYear: number;
    individualEligible: boolean;
    groupEligible: boolean;
    subgroupEligible: boolean;
    mvpRegistered: boolean;
  } | null;
  orgSubmissions: any[];
  orgSubmissionsLoading: boolean;
  currentStep: number;
  isLoading: boolean;
  isSaving: boolean;
  isSubmitting: boolean;
  error: string | null;
  loadSuccess: boolean;
  saveSuccess: boolean;
  submitSuccess: boolean;
  cehrtValidation: {
    isValidating: boolean;
    isValid: boolean | null;
    error: string | null;
  };
  multiPacketMode: {
    enabled: boolean;
    mode: "single" | "both";
    currentType: "traditional" | "mvp" | null;
    nextType: "traditional" | "mvp" | null;
    traditionalPacketId: number | null;
    mvpPacketId: number | null;
    existingTraditionalPacketId: number | null;
    existingMvpPacketId: number | null;
  };
  availableTins: OrganizationTin[];
  loadingTins: boolean;
  sharedDataSyncNeeded: boolean;
  drcfSigningError: string | null;
  qualityMeasures: DashboardMeasureDetailProd[];
  qualityMeasuresLoading: boolean;
  drcfEnvelopesLoading: boolean;
  lastQualityMeasureClinicians: string[] | null;
  packetOrgQuestionnaire: MipsQuestionnaire | null;
  reweighting: ReweightingResult | null;
  reweightingLoading: boolean;
  submissionSuccessData: {
    packetId: number | null;
    tin: string | null;
    npi: string | null;
    isIndividual: boolean;
    performanceYear: number | null;
    externalSubmissionId: string | null;
    submittedDate: string | null;
    finalScore: number | null;
    status: string | null;
    measureScores: { measureId: string; score: number | null; message: string | null }[];
    scoreLoading: boolean;
    scoreError: string | null;
  } | null;
}

export const initialState: State = {
  traditionalPacket: null,
  mvpPacket: null,
  providersForSelectedPractice: [],
  loadingProviders: false,
  eligibilityCheckResult: null,
  orgSubmissions: [],
  orgSubmissionsLoading: false,
  currentStep: 1,
  isLoading: false,
  isSaving: false,
  isSubmitting: false,
  error: null,
  loadSuccess: false,
  saveSuccess: false,
  submitSuccess: false,
  cehrtValidation: {
    isValidating: false,
    isValid: null,
    error: null,
  },
  multiPacketMode: {
    enabled: false,
    mode: "single",
    currentType: null,
    nextType: null,
    traditionalPacketId: null,
    mvpPacketId: null,
    existingTraditionalPacketId: null,
    existingMvpPacketId: null,
  },
  availableTins: [],
  loadingTins: false,
  sharedDataSyncNeeded: false,
  drcfSigningError: null,
  qualityMeasures: [],
  qualityMeasuresLoading: false,
  drcfEnvelopesLoading: false,
  lastQualityMeasureClinicians: null,
  packetOrgQuestionnaire: null,
  reweighting: null,
  reweightingLoading: false,
  submissionSuccessData: null,
};

// --- Helper Functions ---

function getActivePacket(state: State): MipsPacket | null {
  if (state.multiPacketMode.enabled) {
    return state.multiPacketMode.currentType === "mvp"
      ? state.mvpPacket
      : state.traditionalPacket;
  }
  return state.traditionalPacket || state.mvpPacket;
}

function updateActivePacket(
  state: State,
  updater: (packet: MipsPacket | null) => MipsPacket | null,
): Pick<State, "traditionalPacket" | "mvpPacket"> {
  const mode = state.multiPacketMode;

  if (!mode.enabled) {
    if (state.traditionalPacket) {
      return {
        traditionalPacket: updater(state.traditionalPacket),
        mvpPacket: state.mvpPacket,
      };
    } else if (state.mvpPacket) {
      return {
        traditionalPacket: state.traditionalPacket,
        mvpPacket: updater(state.mvpPacket),
      };
    }
    return { traditionalPacket: updater(null), mvpPacket: state.mvpPacket };
  }

  if (mode.currentType === "mvp") {
    return {
      traditionalPacket: state.traditionalPacket,
      mvpPacket: updater(state.mvpPacket),
    };
  }
  return {
    traditionalPacket: updater(state.traditionalPacket),
    mvpPacket: state.mvpPacket,
  };
}

function updateBothPacketsSharedData<
  T extends "tinDetails" | "settings" | "promotingInteroperability",
>(
  state: State,
  property: T,
  value: MipsPacket[T],
): Pick<State, "traditionalPacket" | "mvpPacket"> {
  if (!state.multiPacketMode.enabled) {
    return updateActivePacket(state, (packet) =>
      packet ? { ...packet, [property]: value } : null,
    );
  }

  return {
    traditionalPacket: state.traditionalPacket
      ? { ...state.traditionalPacket, [property]: value }
      : state.traditionalPacket,
    mvpPacket: state.mvpPacket
      ? { ...state.mvpPacket, [property]: value }
      : state.mvpPacket,
  };
}

function applyQualityScores(quality: any, response: any, isMvp: boolean = false): any {
  if (!response) return quality;
  const qs = response.qualityScores;
  if (!qs) return quality;
  let result = { ...quality };
  if (qs.measureScores?.length) {
    const scoreMap = new Map<string, any>(qs.measureScores.map((s: any) => [s.measureCode, s]));
    const applyScores = (measures: any[]) => (measures || []).map((measure: any) => {
      const score = scoreMap.get(measure.measureId);
      if (!score) return measure;
      return { ...measure, calculatedPoints: score.achievementPoints };
    });
    result = {
      ...result,
      allMeasures: applyScores(result.allMeasures ?? []),
      traditionalMeasures: applyScores(result.traditionalMeasures ?? []),
      mvpMeasures: applyScores(result.mvpMeasures ?? []),
    };
  }
  return {
    ...result,
    estimatedPoints: (isMvp ? qs.mvpPoints : qs.traditionalPoints) ?? quality.estimatedPoints,
    estimatedTotal: (isMvp ? qs.mvpTotal : qs.traditionalTotal) ?? quality.estimatedTotal,
    estimatedWeight: (isMvp ? qs.mvpWeightedScore : qs.traditionalWeightedScore) ?? quality.estimatedWeight,
    maxWeight: quality.maxWeight,
  };
}

function updateBothPacketsQuality(
  state: State,
  quality: any,
  traditionalMeasureIds: string[],
  mvpMeasureIds: string[],
  response?: any,
  mvpResponse?: any,
  reportingType?: string,
): Pick<State, "traditionalPacket" | "mvpPacket"> {
  if (!state.multiPacketMode.enabled) {
    const isMvp = reportingType === 'mvp';
    const scoredQuality = applyQualityScores(quality, response, isMvp);
    return updateActivePacket(state, (packet) =>
      packet ? { ...packet, quality: scoredQuality } : null,
    );
  }

  const tradQuality = applyQualityScores({ ...quality, selectedMeasureIds: traditionalMeasureIds }, response, false);
  const mvpQuality = applyQualityScores({ ...quality, selectedMeasureIds: mvpMeasureIds }, mvpResponse, true);

  return {
    traditionalPacket: state.traditionalPacket
      ? { ...state.traditionalPacket, quality: tradQuality, step5Complete: true }
      : state.traditionalPacket,
    mvpPacket: state.mvpPacket
      ? { ...state.mvpPacket, quality: mvpQuality, step5Complete: true }
      : state.mvpPacket,
  };
}

function mergeWithEstimatedScores(
  newPacket: MipsPacket,
  existingPacket: MipsPacket | null,
): MipsPacket {
  if (!existingPacket || existingPacket.id !== newPacket.id) {
    return newPacket;
  }

  return {
    ...newPacket,
    quality:
      newPacket.quality && existingPacket.quality
        ? {
            ...newPacket.quality,
            estimatedPoints: existingPacket.quality.estimatedPoints,
            estimatedBonus: existingPacket.quality.estimatedBonus,
            estimatedTotal: existingPacket.quality.estimatedTotal,
            estimatedWeight: existingPacket.quality.estimatedWeight,
            maxWeight: existingPacket.quality.maxWeight,
          }
        : newPacket.quality,
    promotingInteroperability:
      newPacket.promotingInteroperability &&
      existingPacket.promotingInteroperability
        ? {
            ...newPacket.promotingInteroperability,
            estimatedPoints:
              existingPacket.promotingInteroperability.estimatedPoints,
            estimatedBonus:
              existingPacket.promotingInteroperability.estimatedBonus,
            estimatedTotal:
              existingPacket.promotingInteroperability.estimatedTotal,
            estimatedWeight:
              existingPacket.promotingInteroperability.estimatedWeight,
            maxWeight: existingPacket.promotingInteroperability.maxWeight,
          }
        : newPacket.promotingInteroperability,
    improvementActivities:
      newPacket.improvementActivities && existingPacket.improvementActivities
        ? {
            ...newPacket.improvementActivities,
            estimatedPoints:
              existingPacket.improvementActivities.estimatedPoints,
            estimatedWeight:
              existingPacket.improvementActivities.estimatedWeight,
            maxWeight: existingPacket.improvementActivities.maxWeight,
          }
        : newPacket.improvementActivities,
  };
}

function assignPacketByReportingType(
  state: State,
  packet: MipsPacket,
): Pick<State, "traditionalPacket" | "mvpPacket"> {
  const reportingType = packet.eligibility?.reportingType;
  const isMvp = reportingType === "mvp";

  return {
    traditionalPacket: !isMvp
      ? mergeWithEstimatedScores(packet, state.traditionalPacket)
      : state.traditionalPacket,
    mvpPacket: isMvp
      ? mergeWithEstimatedScores(packet, state.mvpPacket)
      : state.mvpPacket,
  };
}

// --- Reducer ---

export const reducer = createReducer(
  initialState,

  // Multi-Packet Mode

  on(MipsPacketActions.setMultiPacketMode, (state, action) => {
    const enteringBothMode =
      action.mode === "both" && !state.multiPacketMode.enabled;
    const bothPacketsExist = state.traditionalPacket && state.mvpPacket;

    let syncedMvp = state.mvpPacket;
    let needsDbSync = false;

    if (enteringBothMode && bothPacketsExist) {
      const trad = state.traditionalPacket!;
      const mvp = state.mvpPacket!;

      const tinDetailsDiffer =
        JSON.stringify(trad.tinDetails) !== JSON.stringify(mvp.tinDetails);
      const settingsDiffer =
        JSON.stringify(trad.settings) !== JSON.stringify(mvp.settings);
      const piDiffer =
        JSON.stringify(trad.promotingInteroperability) !==
        JSON.stringify(mvp.promotingInteroperability);

      if (tinDetailsDiffer || settingsDiffer || piDiffer) {
        syncedMvp = {
          ...mvp,
          ...(tinDetailsDiffer && { tinDetails: trad.tinDetails }),
          ...(settingsDiffer && { settings: trad.settings }),
          ...(piDiffer && {
            promotingInteroperability: trad.promotingInteroperability,
          }),
        };
        needsDbSync = true;
      }
    }

    return {
      ...state,
      traditionalPacket: state.traditionalPacket,
      mvpPacket: syncedMvp,
      multiPacketMode: {
        enabled: action.mode === "both",
        mode: action.mode,
        currentType: action.currentType || null,
        nextType: action.nextType || null,
        traditionalPacketId:
          action.traditionalPacketId ||
          state.multiPacketMode.traditionalPacketId,
        mvpPacketId: action.mvpPacketId || state.multiPacketMode.mvpPacketId,
        existingTraditionalPacketId: action.existingTraditionalPacketId || null,
        existingMvpPacketId: action.existingMvpPacketId || null,
      },
      sharedDataSyncNeeded: needsDbSync,
    };
  }),

  on({ type: "[MipsPacket] Sync Shared Data Completed" } as any, (state) => ({
    ...state,
    sharedDataSyncNeeded: false,
  })),

  on(MipsPacketActions.clearMultiPacketMode, (state) => ({
    ...state,
    traditionalPacket: null,
    mvpPacket: null,
    multiPacketMode: {
      enabled: false,
      mode: "single" as const,
      currentType: null,
      nextType: null,
      traditionalPacketId: null,
      mvpPacketId: null,
      existingTraditionalPacketId: null,
      existingMvpPacketId: null,
    },
  })),

  // CEHRT Validation

  on(MipsPacketActions.validateCEHRTID, (state) => ({
    ...state,
    cehrtValidation: { isValidating: true, isValid: null, error: null },
  })),
  on(MipsPacketActions.validateCEHRTIDSuccess, (state, { isValid }) => ({
    ...state,
    cehrtValidation: { isValidating: false, isValid, error: null },
  })),
  on(MipsPacketActions.validateCEHRTIDFailure, (state, { error }) => ({
    ...state,
    cehrtValidation: { isValidating: false, isValid: null, error },
  })),
  on(MipsPacketActions.resetCEHRTValidation, (state) => ({
    ...state,
    cehrtValidation: { isValidating: false, isValid: null, error: null },
  })),

  // Load Existing Packet

  on(MipsPacketActions.loadExistingPacket, (state) => ({
    ...state,
    isLoading: true,
    error: null,
    submissionSuccessData: null,
  })),
  on(MipsPacketActions.loadExistingPacketSuccess, (state, { packet }) => {
    const isMvp = packet.eligibility?.reportingType === "mvp";

    return {
      ...state,
      ...assignPacketByReportingType(state, packet),
      isLoading: false,
      loadSuccess: true,
      error: null,
      packetOrgQuestionnaire: null,
      cehrtValidation: packet.settings?.cehrtId
        ? { isValidating: false, isValid: true, error: null }
        : state.cehrtValidation,
      multiPacketMode: !state.multiPacketMode.enabled
        ? {
            ...state.multiPacketMode,
            currentType: (isMvp ? "mvp" : "traditional") as
              | "traditional"
              | "mvp",
          }
        : state.multiPacketMode,
    };
  }),
  on(MipsPacketActions.loadExistingPacketFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    loadSuccess: false,
    error,
  })),

  // Load Both Packets

  on(MipsPacketActions.loadBothPackets, (state) => ({
    ...state,
    isLoading: true,
    error: null,
    submissionSuccessData: null,
  })),
  on(
    MipsPacketActions.loadBothPacketsSuccess,
    (state, { traditionalPacket, mvpPacket }) => ({
      ...state,
      traditionalPacket,
      mvpPacket,
      isLoading: false,
      loadSuccess: true,
      error: null,
    }),
  ),
  on(MipsPacketActions.loadBothPacketsFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    loadSuccess: false,
    error,
  })),

  // Get or Create Packet

  on(MipsPacketActions.getOrCreatePacket, (state) => ({
    ...state,
    isSaving: true,
    error: null,
    submissionSuccessData: null,
  })),
  on(MipsPacketActions.getOrCreatePacketSuccess, (state, { packet }) => {
    const isMvp = packet.eligibility?.reportingType === "mvp";

    return {
      ...state,
      ...assignPacketByReportingType(state, packet),
      currentStep: 3,
      isSaving: false,
      saveSuccess: true,
      error: null,
      multiPacketMode: !state.multiPacketMode.enabled
        ? {
            ...state.multiPacketMode,
            currentType: (isMvp ? "mvp" : "traditional") as
              | "traditional"
              | "mvp",
          }
        : state.multiPacketMode,
    };
  }),
  on(MipsPacketActions.getOrCreatePacketFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    saveSuccess: false,
    error,
  })),

  // Providers

  on(MipsPacketActions.loadProvidersForSelectedPractice, (state) => ({
    ...state,
    loadingProviders: true,
  })),
  on(
    MipsPacketActions.loadProvidersForSelectedPracticeSuccess,
    (state, { providers }) => ({
      ...state,
      providersForSelectedPractice: providers,
      loadingProviders: false,
    }),
  ),
  on(
    MipsPacketActions.loadProvidersForSelectedPracticeFailure,
    (state, { error }) => ({
      ...state,
      loadingProviders: false,
      error,
    }),
  ),

  on(MipsPacketActions.loadTinsForPractice, (state) => ({
    ...state,
    loadingTins: true,
    availableTins: [],
  })),
  on(MipsPacketActions.loadTinsForPracticeSuccess, (state, { tins }) => ({
    ...state,
    availableTins: tins,
    loadingTins: false,
  })),
  on(MipsPacketActions.loadTinsForPracticeFailure, (state, { error }) => ({
    ...state,
    loadingTins: false,
    availableTins: [],
    error,
  })),

  // TIN Details

  on(MipsPacketActions.saveTINDetails, (state) => ({
    ...state,
    isSaving: true,
    error: null,
    saveSuccess: false,
  })),
  on(MipsPacketActions.saveTINDetailsSuccess, (state, { tinDetails }) => ({
    ...state,
    ...updateBothPacketsSharedData(state, "tinDetails", tinDetails),
    isSaving: false,
    saveSuccess: true,
    error: null,
  })),
  on(MipsPacketActions.saveTINDetailsFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    saveSuccess: false,
    error,
  })),

  // Settings

  on(MipsPacketActions.saveSettings, (state) => ({
    ...state,
    isSaving: true,
    error: null,
    saveSuccess: false,
  })),
  on(MipsPacketActions.saveSettingsSuccess, (state, { settings }) => {
    const withSettings = updateBothPacketsSharedData(state, "settings", settings);
    return {
      ...state,
      ...withSettings,
      isSaving: false,
      saveSuccess: true,
      error: null,
    };
  }),
  on(MipsPacketActions.saveSettingsFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    saveSuccess: false,
    error,
  })),

  // MVP Registration

  on(
    MipsPacketActions.refreshMVPRegistrationSuccess,
    (state, { submissionId, mvpId }) => {
      if (!mvpId) {
        return { ...state, error: null };
      }

      const traditionalMatches =
        state.traditionalPacket?.id === submissionId;
      const mvpMatches = state.mvpPacket?.id === submissionId;

      return {
        ...state,
        traditionalPacket: traditionalMatches
          ? { ...state.traditionalPacket!, mvpId }
          : state.traditionalPacket,
        mvpPacket: mvpMatches
          ? { ...state.mvpPacket!, mvpId }
          : state.mvpPacket,
        error: null,
      };
    },
  ),
  on(MipsPacketActions.refreshMVPRegistrationFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  // Quality Measures

  on(MipsPacketActions.loadQualityMeasures, (state, { filter }) => ({
    ...state,
    qualityMeasuresLoading: true,
    lastQualityMeasureClinicians: filter.clinician,
  })),
  on(MipsPacketActions.loadQualityMeasuresSuccess, (state, { measures }) => {
    const enrichPacket = (packet: MipsPacket | null) =>
      packet?.quality
        ? { ...packet, quality: enrichQualityData(packet.quality, measures) }
        : packet;

    return {
      ...state,
      qualityMeasures: measures,
      qualityMeasuresLoading: false,
      traditionalPacket: enrichPacket(state.traditionalPacket),
      mvpPacket: enrichPacket(state.mvpPacket),
    };
  }),
  on(MipsPacketActions.loadQualityMeasuresFailure, (state) => ({
    ...state,
    qualityMeasuresLoading: false,
  })),

  on(MipsPacketActions.loadPacketOrgQuestionnaire, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(MipsPacketActions.loadPacketOrgQuestionnaireSuccess, (state, { questionnaire }) => ({
    ...state,
    packetOrgQuestionnaire: questionnaire,
    isLoading: false,
  })),
  on(MipsPacketActions.loadPacketOrgQuestionnaireFailure, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(MipsQuestionnaireActions.submitMIPSQuestionnaire, (state, { mipsQuestionnaire }) => ({
    ...state,
    packetOrgQuestionnaire: mipsQuestionnaire,
  })),
  on(MipsQuestionnaireActions.updateMIPSQuestionnaire, (state, { mipsQuestionnaire }) => ({
    ...state,
    packetOrgQuestionnaire: mipsQuestionnaire,
  })),

  on(MipsPacketActions.loadMipsReweighting, (state) => ({
    ...state,
    reweightingLoading: true,
  })),
  on(MipsPacketActions.loadMipsReweightingSuccess, (state, { data }) => ({
    ...state,
    reweighting: data,
    reweightingLoading: false,
  })),
  on(MipsPacketActions.loadMipsReweightingFailure, (state) => ({
    ...state,
    reweightingLoading: false,
  })),

  // Quality Measurements

  on(MipsPacketActions.saveQualityMeasurements, (state) => ({
    ...state,
    isSaving: true,
    error: null,
    saveSuccess: false,
  })),
  on(
    MipsPacketActions.saveQualityMeasurementsSuccess,
    (state, { response, mvpResponse, quality, traditionalMeasureIds, mvpMeasureIds, reportingType }) => ({
      ...state,
      ...updateBothPacketsQuality(
        state,
        quality,
        traditionalMeasureIds,
        mvpMeasureIds,
        response,
        mvpResponse,
        reportingType,
      ),
      isSaving: false,
      saveSuccess: true,
      error: null,
    }),
  ),
  on(MipsPacketActions.saveQualityMeasurementsFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    saveSuccess: false,
    error,
  })),

  // Promoting Interoperability - Attestations

  on(MipsPacketActions.savePIAttestations, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),
  on(MipsPacketActions.savePIAttestationsSuccess, (state, { response, performanceStart, performanceEnd }) => {
    const activePacketYear = getActivePacket(state)?.performanceYear;
    const attestationsComplete =
      parseAttestationBool(response.piAuditAttestation) === true &&
      parseAttestationBool(response.oncDirectReview) === true &&
      parseAttestationBool(response.interoperabilityAttestation) === true &&
      parseAttestationBool(response.securityRiskAnalysis) === true &&
      (activePacketYear != null && activePacketYear >= 2026
        ? parseAttestationBool(response.securityRiskAnalysisImplemented) === true
        : true) &&
      parseAttestationBool(response.saferGuidesAttestation) === true;

    return {
      ...state,
      ...updateActivePacket(state, (packet) =>
        packet
          ? {
              ...packet,
              promotingInteroperability: {
                    cehrtValidationCount: 0,
                    objectivesMet: 0,
                    measures: [] as any[],
                    estimatedPoints: 0,
                    estimatedBonus: 0,
                    estimatedTotal: 0,
                    estimatedWeight: 0,
                    maxWeight: 25,
                    ...(packet.promotingInteroperability || {}),
                    performanceStart,
                    performanceEnd,
                    attestations: {
                      piAuditAttestation: parseAttestationBool(response.piAuditAttestation) ?? false,
                      oncDirectReview: parseAttestationBool(response.oncDirectReview),
                      interoperabilityAttestation: parseAttestationBool(
                        response.interoperabilityAttestation,
                      ),
                      securityRiskAnalysis: parseAttestationBool(
                        response.securityRiskAnalysis,
                      ),
                      securityRiskAnalysisImplemented: parseAttestationBool(
                        response.securityRiskAnalysisImplemented,
                      ),
                      saferGuidesAttestation: parseAttestationBool(
                        response.saferGuidesAttestation,
                      ),
                    },
                    attested: parseAttestationBool(response.piAuditAttestation) === true,
                    attestationsComplete,
                  },
            }
          : null,
      ),
      isSaving: false,
      saveSuccess: true,
      error: null,
    };
  }),
  on(MipsPacketActions.savePIAttestationsFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    saveSuccess: false,
    error,
  })),

  // Promoting Interoperability - Measurements

  on(MipsPacketActions.savePIMeasurements, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),
  on(MipsPacketActions.savePIMeasurementsSuccess, (state, { response, fullMeasures, exclusionIds }) => {
    const currentPacket = getActivePacket(state);

    const scoredMeasures = response.measureScores?.length
      ? fullMeasures.map((measure) => {
          const score = response.measureScores.find(
            (s) => s.measureId === measure.measureId,
          );
          if (!score) return measure;
          return {
            ...measure,
            points: score.computedPoints,
            effectiveMaxPoints: score.effectiveMaxPoints,
            redistributedPoints: score.redistributedPoints,
            bonusPoints: score.bonusPoints,
            showBonusPoints: score.showBonusPoints,
          };
        })
      : fullMeasures;

    const updatedPI = {
      cehrtValidationCount: 0,
      attested: false,
      attestations: {},
      objectivesMet: 0,
      ...(currentPacket?.promotingInteroperability || {}),
      measures: scoredMeasures,
      estimatedPoints: response.piEstimatedPoints,
      estimatedBonus: response.piEstimatedBonus,
      estimatedTotal: response.piEstimatedTotal,
      estimatedWeight: response.piEstimatedWeight,
      maxWeight: response.piMaxWeight,
      allRequiredComplete: response.allRequiredComplete,
      attestationsComplete: response.attestationsComplete,
      incompleteMeasureIds: response.incompleteMeasureIds,
      allExclusionIds: exclusionIds,
    };

    return {
      ...state,
      ...updateBothPacketsSharedData(
        state,
        "promotingInteroperability",
        updatedPI,
      ),
      isSaving: false,
      saveSuccess: true,
      error: null,
    };
  }),
  on(MipsPacketActions.savePIMeasurementsFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    saveSuccess: false,
    error,
  })),

  // Promoting Interoperability - Documents

  on(MipsPacketActions.uploadPIDocument, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),
  on(
    MipsPacketActions.uploadPIDocumentSuccess,
    (state, { documentId, fileName, documentUrl }) => ({
      ...state,
      ...updateActivePacket(state, (packet) =>
        packet && packet.promotingInteroperability
          ? {
              ...packet,
              promotingInteroperability: {
                ...packet.promotingInteroperability,
                documents: [
                  ...(packet.promotingInteroperability.documents || []),
                  { documentId, fileName, documentUrl },
                ],
              },
            }
          : packet,
      ),
      isSaving: false,
      error: null,
    }),
  ),
  on(MipsPacketActions.uploadPIDocumentFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),
  on(MipsPacketActions.deletePIDocument, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),
  on(MipsPacketActions.deletePIDocumentSuccess, (state, { documentId }) => ({
    ...state,
    ...updateActivePacket(state, (packet) =>
      packet && packet.promotingInteroperability
        ? {
            ...packet,
            promotingInteroperability: {
              ...packet.promotingInteroperability,
              documents: (
                packet.promotingInteroperability.documents || []
              ).filter((d: any) => d.documentId !== documentId),
            },
          }
        : packet,
    ),
    isSaving: false,
    error: null,
  })),
  on(MipsPacketActions.deletePIDocumentFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),

  // Improvement Activities

  on(MipsPacketActions.saveIAMeasurements, (state) => ({
    ...state,
    isSaving: true,
    error: null,
    saveSuccess: false,
  })),
  on(MipsPacketActions.saveIAMeasurementsSuccess, (state, { packetId, measurements, response, dateRangeStart, dateRangeEnd }) => {
    const measurementMap = new Map(measurements.map(m => [m.measureId, m.booleanValue]));

    const updateIA = (packet: MipsPacket | null) => {
      if (!packet) return packet;

      const existingIA = packet.improvementActivities;

      const scoreMap = new Map(
        (response.measureScores || []).map((s) => [s.measureId, s]),
      );

      const updatedActivities = (existingIA?.activities || []).map((activity: any) => {
        const selected = measurementMap.get(activity.activityId) === true;
        const score = scoreMap.get(activity.activityId);
        return {
          ...activity,
          selected,
          ...(score ? { points: score.points, isHighWeighted: score.isHighWeighted } : {}),
        };
      });

      const existingIds = new Set(updatedActivities.map((a: any) => a.activityId));
      const newActivities = measurements
        .filter(m => !existingIds.has(m.measureId) && m.booleanValue)
        .map(m => {
          const score = scoreMap.get(m.measureId);
          return {
            activityId: m.measureId,
            selected: true,
            ...(score ? { points: score.points, isHighWeighted: score.isHighWeighted } : {}),
          };
        });

      return {
        ...packet,
        improvementActivities: {
          minimumActivitiesRequired: 1,
          ...(existingIA || {}),
          estimatedPoints: response.iaEstimatedPoints,
          estimatedWeight: response.iaEstimatedWeight,
          maxWeight: response.iaMaxWeight,
          dateRangeStart,
          dateRangeEnd,
          activities: [...updatedActivities, ...newActivities],
        },
      };
    };

    return {
      ...state,
      traditionalPacket:
        state.traditionalPacket?.id === packetId
          ? updateIA(state.traditionalPacket)
          : state.traditionalPacket,
      mvpPacket:
        state.mvpPacket?.id === packetId
          ? updateIA(state.mvpPacket)
          : state.mvpPacket,
      isSaving: false,
      saveSuccess: true,
      error: null,
    };
  }),
  on(MipsPacketActions.saveIAMeasurementsFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    saveSuccess: false,
    error,
  })),
  // Improvement Activities - Documents

  on(MipsPacketActions.uploadIADocument, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),
  on(
    MipsPacketActions.uploadIADocumentSuccess,
    (state, { packetId, documentId, fileName, measureId, documentUrl }) => {
      const updateIA = (packet: MipsPacket | null) => {
        if (!packet) return packet;

        const existingActivities =
          packet.improvementActivities?.activities || [];
        const updatedActivities = existingActivities.map((activity) =>
          activity.activityId === measureId
            ? { ...activity, documentId, documentName: fileName, documentUrl }
            : activity,
        );

        return {
          ...packet,
          improvementActivities: {
            dateRangeStart: "",
            dateRangeEnd: "",
            minimumActivitiesRequired: 1,
            estimatedPoints: 0,
            estimatedWeight: 0,
            maxWeight: 15,
            ...(packet.improvementActivities || {}),
            activities: updatedActivities,
          },
        };
      };

      return {
        ...state,
        traditionalPacket:
          state.traditionalPacket?.id === packetId
            ? updateIA(state.traditionalPacket)
            : state.traditionalPacket,
        mvpPacket:
          state.mvpPacket?.id === packetId
            ? updateIA(state.mvpPacket)
            : state.mvpPacket,
        isSaving: false,
        error: null,
      };
    },
  ),
  on(MipsPacketActions.uploadIADocumentFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),
  on(MipsPacketActions.deleteIADocument, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),
  on(MipsPacketActions.deleteIADocumentSuccess, (state, { packetId, measureId }) => {
    const updateIA = (packet: MipsPacket | null) => {
      if (!packet || !packet.improvementActivities) return packet;
      return {
        ...packet,
        improvementActivities: {
          ...packet.improvementActivities,
          activities: packet.improvementActivities.activities.map(
            (activity: any) =>
              activity.activityId === measureId
                ? {
                    ...activity,
                    documentId: undefined,
                    documentName: undefined,
                    documentUrl: undefined,
                  }
                : activity,
          ),
        },
      };
    };

    return {
      ...state,
      traditionalPacket:
        state.traditionalPacket?.id === packetId
          ? updateIA(state.traditionalPacket)
          : state.traditionalPacket,
      mvpPacket:
        state.mvpPacket?.id === packetId
          ? updateIA(state.mvpPacket)
          : state.mvpPacket,
      isSaving: false,
      error: null,
    };
  }),
  on(MipsPacketActions.deleteIADocumentFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),
  on(MipsPacketActions.downloadIADocument, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(MipsPacketActions.downloadIADocumentSuccess, (state) => ({
    ...state,
    isLoading: false,
    error: null,
  })),
  on(MipsPacketActions.downloadIADocumentFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  // DRCF

  on(MipsPacketActions.loadDRCFEnvelopes, (state) => ({
    ...state,
    drcfEnvelopesLoading: true,
  })),
  on(MipsPacketActions.loadDRCFEnvelopesSuccess, (state, { submissionId, documents }) => {
    const drcfStatus = {
      documents,
      allDocumentsSigned: documents.every(
        (doc) => doc.status === "completed",
      ),
    };

    const updatePacket = (packet: MipsPacket | null) =>
      packet && packet.id === submissionId
        ? { ...packet, drcfStatus }
        : packet;

    return {
      ...state,
      traditionalPacket: updatePacket(state.traditionalPacket),
      mvpPacket: updatePacket(state.mvpPacket),
      drcfEnvelopesLoading: false,
      error: null,
    };
  }),
  on(MipsPacketActions.loadDRCFEnvelopesFailure, (state, { error }) => ({
    ...state,
    drcfEnvelopesLoading: false,
    error,
  })),
  on(MipsPacketActions.updateDRCFStatus, (state) => ({
    ...state,
    isSaving: true,
    error: null,
    saveSuccess: false,
  })),
  on(MipsPacketActions.updateDRCFStatusSuccess, (state, { drcfStatus }) => ({
    ...state,
    ...updateActivePacket(state, (packet) =>
      packet ? { ...packet, drcfStatus } : null,
    ),
    isSaving: false,
    saveSuccess: true,
    error: null,
  })),
  on(MipsPacketActions.updateDRCFStatusFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    saveSuccess: false,
    error,
  })),
  on(MipsPacketActions.createDRCFEnvelope, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),
  on(MipsPacketActions.createDRCFEnvelopeSuccess, (state) => ({
    ...state,
    isSaving: false,
    error: null,
  })),
  on(MipsPacketActions.createDRCFEnvelopeFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),
  on(MipsPacketActions.openDRCFSigning, (state) => ({
    ...state,
    drcfSigningError: null,
  })),
  on(MipsPacketActions.openDRCFSigningSuccess, (state) => ({
    ...state,
    drcfSigningError: null,
  })),
  on(MipsPacketActions.openDRCFSigningFailure, (state, { error }) => ({
    ...state,
    drcfSigningError: error,
  })),

  // TIN Document

  on(MipsPacketActions.uploadTINDocument, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),
  on(
    MipsPacketActions.uploadTINDocumentSuccess,
    (state, { documentUrl, documentName }) => {
      const currentPacket = getActivePacket(state);
      if (!currentPacket?.tinDetails) {
        return { ...state, isSaving: false, error: null };
      }

      const updatedTinDetails = {
        ...currentPacket.tinDetails,
        documentUrl,
        documentName,
        tinStatus: "pending" as const,
        approvedBy: undefined,
        approvedDate: undefined,
        rejectionReason: undefined,
      };

      return {
        ...state,
        ...updateBothPacketsSharedData(state, "tinDetails", updatedTinDetails),
        isSaving: false,
        error: null,
      };
    },
  ),
  on(MipsPacketActions.uploadTINDocumentFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),
  on(MipsPacketActions.deleteTINDocument, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),
  on(MipsPacketActions.deleteTINDocumentSuccess, (state) => {
    const currentPacket = getActivePacket(state);
    if (!currentPacket?.tinDetails) {
      return { ...state, isSaving: false, error: null };
    }

    const updatedTinDetails = {
      ...currentPacket.tinDetails,
      documentUrl: undefined,
      documentName: undefined,
    };

    return {
      ...state,
      ...updateBothPacketsSharedData(state, "tinDetails", updatedTinDetails),
      isSaving: false,
      error: null,
    };
  }),
  on(MipsPacketActions.deleteTINDocumentFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),

  // TIN Approval

  on(MipsPacketActions.approveTIN, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),
  on(MipsPacketActions.approveTINSuccess, (state, { tinDetails }) => ({
    ...state,
    ...updateBothPacketsSharedData(state, "tinDetails", tinDetails),
    isSaving: false,
    error: null,
  })),
  on(MipsPacketActions.approveTINFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),
  on(MipsPacketActions.rejectTIN, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),
  on(MipsPacketActions.rejectTINSuccess, (state, { tinDetails }) => ({
    ...state,
    ...updateBothPacketsSharedData(state, "tinDetails", tinDetails),
    isSaving: false,
    error: null,
  })),
  on(MipsPacketActions.rejectTINFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),
  on(MipsPacketActions.requestTINApproval, (state) => ({
    ...state,
    isSaving: true,
    error: null,
  })),
  on(MipsPacketActions.requestTINApprovalSuccess, (state) => ({
    ...state,
    isSaving: false,
    error: null,
  })),
  on(MipsPacketActions.requestTINApprovalFailure, (state, { error }) => ({
    ...state,
    isSaving: false,
    error,
  })),

  // Navigation & Submission

  on(MipsPacketActions.setCurrentStep, (state, { stepNumber }) => ({
    ...state,
    currentStep: stepNumber,
    saveSuccess: false,
  })),
  on(MipsPacketActions.submitPacket, (state) => ({
    ...state,
    isSubmitting: true,
    error: null,
    submitSuccess: false,
  })),
  on(MipsPacketActions.submitPacketSuccess, (state) => ({
    ...state,
    isSubmitting: false,
    submitSuccess: true,
    error: null,
  })),
  on(MipsPacketActions.submitPacketFailure, (state, { error }) => ({
    ...state,
    isSubmitting: false,
    submitSuccess: false,
    error,
  })),
  on(MipsPacketActions.submitMultiplePackets, (state) => ({
    ...state,
    isSubmitting: true,
    error: null,
    submitSuccess: false,
  })),
  on(MipsPacketActions.submitMultiplePacketsSuccess, (state) => ({
    ...state,
    isSubmitting: false,
    submitSuccess: true,
    error: null,
  })),
  on(MipsPacketActions.submitMultiplePacketsFailure, (state, { error }) => ({
    ...state,
    isSubmitting: false,
    submitSuccess: false,
    error,
  })),

  on(MipsPacketActions.setSubmissionSuccessData, (state, { packetId, tin, npi, isIndividual, performanceYear, externalSubmissionId, submittedDate }) => ({
    ...state,
    submitSuccess: true,
    submissionSuccessData: {
      packetId,
      tin,
      npi,
      isIndividual,
      performanceYear,
      externalSubmissionId,
      submittedDate,
      finalScore: null,
      status: null,
      measureScores: [],
      scoreLoading: false,
      scoreError: null,
    },
  })),
  on(MipsPacketActions.getSubmissionScore, (state) => ({
    ...state,
    submissionSuccessData: state.submissionSuccessData
      ? { ...state.submissionSuccessData, scoreLoading: true, scoreError: null }
      : state.submissionSuccessData,
  })),
  on(MipsPacketActions.getSubmissionScoreSuccess, (state, { score }) => ({
    ...state,
    submissionSuccessData: state.submissionSuccessData
      ? { ...state.submissionSuccessData, finalScore: score.data?.mips?.value ?? null, scoreLoading: false }
      : state.submissionSuccessData,
  })),
  on(MipsPacketActions.getSubmissionScoreFailure, (state, { error }) => ({
    ...state,
    submissionSuccessData: state.submissionSuccessData
      ? { ...state.submissionSuccessData, scoreLoading: false, scoreError: error }
      : state.submissionSuccessData,
  })),

  // Org Submissions

  on(MipsPacketActions.loadOrgSubmissions, (state) => ({
    ...state,
    orgSubmissionsLoading: true,
    error: null,
  })),
  on(MipsPacketActions.loadOrgSubmissionsSuccess, (state, { submissions }) => ({
    ...state,
    orgSubmissions: submissions,
    orgSubmissionsLoading: false,
  })),
  on(MipsPacketActions.loadOrgSubmissionsFailure, (state, { error }) => ({
    ...state,
    orgSubmissionsLoading: false,
    error,
  })),

  // Delete Submission

  on(MipsPacketActions.deleteSubmission, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(MipsPacketActions.deleteSubmissionSuccess, (state, { packetId }) => ({
    ...state,
    isLoading: false,
    orgSubmissions: state.orgSubmissions.filter((p: any) => p.id !== packetId),
  })),
  on(MipsPacketActions.deleteSubmissionFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  // Reset & Clear

  on(MipsPacketActions.resetPacket, () => ({
    ...initialState,
  })),
  on(MipsPacketActions.clearPacket, (state) => ({
    ...state,
    traditionalPacket: null,
    mvpPacket: null,
    eligibilityCheckResult: null,
    currentStep: 1,
    error: null,
    loadSuccess: false,
    saveSuccess: false,
    submitSuccess: false,
    multiPacketMode: initialState.multiPacketMode,
    cehrtValidation: initialState.cehrtValidation,
    sharedDataSyncNeeded: false,
    drcfSigningError: null,
    submissionSuccessData: null,
    qualityMeasures: initialState.qualityMeasures,
    qualityMeasuresLoading: initialState.qualityMeasuresLoading,
    drcfEnvelopesLoading: initialState.drcfEnvelopesLoading,
    lastQualityMeasureClinicians: initialState.lastQualityMeasureClinicians,
    packetOrgQuestionnaire: initialState.packetOrgQuestionnaire,
  })),
  on(MipsPacketActions.clearError, (state) => ({
    ...state,
    error: null,
    drcfSigningError: null,
  })),
);

export const mipsPacketFeature = createFeature({
  name: mipsPacketFeatureKey,
  reducer,
});
